/*
  Lazy images start loading 1250px before they scroll into view, in every browser.

  Chrome already uses about this margin on 4G; Firefox and Safari use a much
  smaller one, so their images tend to pop in as they enter the screen.
  - Starts only after the page's fetchpriority="high" image has loaded (or the
    window load event when there is none), so it never competes with it.
  - Only switches loading from lazy to eager; it never raises fetchpriority.
    The browser raises the priority itself once an image is in the viewport.
  - Horizontal margin 0: off-screen slider and marquee slides stay lazy.
  - Skips the zoom modal (handled by product-zoom-preload.js) and anything
    marked data-lazy-skip.
  - Off when the visitor asked to save data or is on a 2G connection.
*/
(() => {
  if (window.__lazyNearViewport || !('IntersectionObserver' in window)) return;
  window.__lazyNearViewport = true;

  const connection = navigator.connection;
  if (connection && (connection.saveData || /2g/.test(connection.effectiveType || ''))) return;

  const SKIP = 'product-modal, [data-lazy-skip]';
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);
        entry.target.dataset.lazyPromoted = '';
        entry.target.loading = 'eager';
      }
    },
    { rootMargin: '1250px 0px' }
  );

  const observe = (img) => {
    if (img.loading === 'lazy' && !img.closest(SKIP)) observer.observe(img);
  };
  const scan = (root) => {
    if (root.matches && root.matches('img')) observe(root);
    if (root.querySelectorAll) root.querySelectorAll('img[loading="lazy"]').forEach(observe);
  };

  let started = false;
  const start = () => {
    if (started) return;
    started = true;
    scan(document);
    const main = document.getElementById('MainContent');
    if (main) {
      // product recommendations, slides added on variant change
      new MutationObserver((mutations) =>
        mutations.forEach((m) => m.addedNodes.forEach((node) => node.nodeType === 1 && scan(node)))
      ).observe(main, { childList: true, subtree: true });
    }
    document.addEventListener('shopify:section:load', (event) => scan(event.target));
  };

  const lcpImage = document.querySelector('img[fetchpriority="high"]');
  if (lcpImage && !lcpImage.complete) {
    lcpImage.addEventListener('load', start, { once: true });
    lcpImage.addEventListener('error', start, { once: true });
  } else if (lcpImage || document.readyState === 'complete') {
    start();
  } else {
    window.addEventListener('load', start, { once: true });
  }
})();
